import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { io } from 'socket.io-client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessagingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);

  useEffect(() => {
    checkAuth();
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (selectedConv) {
      fetchMessages(selectedConv.conversation_id);
      if (socketRef.current) {
        socketRef.current.emit('join_conversation', { conversation_id: selectedConv.conversation_id });
      }
    }
  }, [selectedConv]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      await fetchConversations();
      initSocket();
    } catch (error) {
      toast.error('Vous devez être connecté');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const initSocket = () => {
    socketRef.current = io(BACKEND_URL, {
      transports: ['websocket'],
      withCredentials: true
    });

    socketRef.current.on('connect', () => {
      console.log('Socket connected');
    });

    socketRef.current.on('new_message', (data) => {
      if (selectedConv && data.conversation_id === selectedConv.conversation_id) {
        setMessages(prev => [...prev, data.message]);
      }
    });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`, { withCredentials: true });
      setConversations(response.data);
      
      const proposalId = searchParams.get('proposal');
      if (proposalId && response.data.length > 0) {
        const conv = response.data.find(c => c.proposal_id === proposalId);
        if (conv) setSelectedConv(conv);
      } else if (response.data.length > 0) {
        setSelectedConv(response.data[0]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConv) return;

    try {
      await axios.post(`${API}/messages`, {
        conversation_id: selectedConv.conversation_id,
        content: newMessage
      }, { withCredentials: true });
      
      setNewMessage('');
    } catch (error) {
      toast.error('Erreur envoi message');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg">
      <Header
        currentUser={user}
        acquirerAdsCount={0}
        onLoginClick={() => {}}
        onAboutClick={() => {}}
        onCreateAdClick={() => navigate('/create-search')}
        onLogout={async () => {
          await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
          navigate('/');
          window.location.reload();
        }}
      />

      <div className="max-w-7xl mx-auto px-2 md:px-4 pb-12">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden" style={{ height: '70vh' }}>
          <div className="grid md:grid-cols-3 h-full">
            <div className="border-r border-gray-200 overflow-y-auto">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-blue-600">💬 Messagerie</h2>
              </div>
              {conversations.length === 0 ? (
                <div className="p-8 text-center text-gray-600 text-sm">
                  Aucune conversation
                </div>
              ) : (
                <div>
                  {conversations.map((conv) => (
                    <div
                      key={conv.conversation_id}
                      onClick={() => setSelectedConv(conv)}
                      className={`p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 ${
                        selectedConv?.conversation_id === conv.conversation_id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-800 truncate text-sm">{conv.other_user?.name || 'Utilisateur'}</h3>
                          <p className="text-xs text-gray-500 truncate">{conv.last_message || 'Nouvelle conversation'}</p>
                        </div>
                        {conv.unread_count?.[user.user_id] > 0 && (
                          <span className="bg-red-600 text-white px-2 py-1 rounded-full text-xs font-bold">
                            {conv.unread_count[user.user_id]}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="md:col-span-2 flex flex-col">
              {!selectedConv ? (
                <div className="flex-1 flex items-center justify-center text-gray-600">
                  Sélectionnez une conversation
                </div>
              ) : (
                <>
                  <div className="p-4 border-b border-gray-200 bg-white">
                    <div className="flex items-center gap-3">
                      <div>
                        <h3 className="font-bold text-gray-800">{selectedConv.other_user?.name}</h3>
                        <p className="text-xs text-gray-500">
                          {selectedConv.proposal_summary?.property_address}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.message_id}
                        className={`flex ${msg.sender_id === user.user_id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-2xl p-4 ${
                            msg.sender_id === user.user_id
                              ? 'bg-blue-600 text-white rounded-br-sm'
                              : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                          }`}
                        >
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-xs mt-1 ${
                            msg.sender_id === user.user_id ? 'text-blue-100' : 'text-gray-500'
                          }`}>
                            {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="p-4 border-t border-gray-200 bg-white">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        placeholder="Votre message..."
                        className="rounded-full"
                      />
                      <Button
                        onClick={sendMessage}
                        size="icon"
                        className="rounded-full bg-blue-600"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagingPage;
